import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

interface JurisdictionCascadeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  licenseTypeName: string;
  isDeactivating: boolean; // true = deactivating license type, false = activating
  affectedConfigurationsCount: number;
  isLoading?: boolean;
  onConfirm: () => void;
}

export function JurisdictionCascadeDialog({
  isOpen,
  onOpenChange,
  licenseTypeName,
  isDeactivating,
  affectedConfigurationsCount,
  isLoading = false,
  onConfirm,
}: JurisdictionCascadeDialogProps) {
  if (affectedConfigurationsCount === 0) {
    return null; // Don't show dialog if no configurations would be affected
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDeactivating ? "Deactivate" : "Reactivate"} License Type
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              {isDeactivating
                ? `Deactivating the "${licenseTypeName}" license type will also deactivate`
                : `Reactivating the "${licenseTypeName}" license type will also reactivate`}{" "}
              <strong>{affectedConfigurationsCount}</strong> jurisdiction
              configuration
              {affectedConfigurationsCount !== 1 ? "s" : ""} for this license
              type.
            </p>
            {isDeactivating ? (
              <p>
                The license type configurations will be deactivated, but the
                jurisdictions themselves will remain active.
              </p>
            ) : (
              <p>
                Both the license type configurations and any previously
                deactivated jurisdictions will be reactivated.
              </p>
            )}
            <p>
              Do you want to proceed with{" "}
              {isDeactivating ? "deactivating" : "reactivating"} the license
              type?
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className={isDeactivating ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isDeactivating
              ? "Deactivate License Type"
              : "Reactivate License Type"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
